import React from "react";
import { Box, Code, Flex, Stack, Text, useColorModeValue } from "@chakra-ui/react";

/**
 * A single stat row with a label on the left and a value on the right.
 * @param {object} props
 * @param {string} props.label - Description label
 * @param {string|number} props.value - Stat value to display
 * @param {boolean} [props.monospace=false] - Whether to render value as code
 * @param {boolean} [props.bold=false] - Whether to render value in large bold text
 * @returns {React.ReactElement}
 */
function IndexStatRow({ label, value, monospace, bold }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" align="center">
            <Text fontSize="sm" color={labelColor}>{label}</Text>
            {monospace
                ? <Code fontSize="sm" borderRadius="6px">{value}</Code>
                : <Text fontSize={bold ? "lg" : "sm"} fontWeight={bold ? "700" : "600"} color={valColor}>{value}</Text>}
        </Flex>
    );
}

/**
 * Displays general index statistics: index name and document count.
 * @param {object} props
 * @param {object} props.data - Index data containing index_name and document_count
 * @returns {React.ReactElement}
 */
function ElasticIndexStats({ data }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box>
            <Text fontSize="md" fontWeight="600" color={titleColor} mb="12px">Index statistics</Text>
            <Box p="14px" bg={bg} borderRadius="12px">
                <Stack spacing="10px">
                    <IndexStatRow label="Index name" value={data?.index_name || "N/A"} monospace />
                    <IndexStatRow label="Total documents" value={data?.document_count || 0} bold />
                </Stack>
            </Box>
        </Box>
    );
}

export default ElasticIndexStats;
